import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Archive, CloudUpload, HardDrive, ShieldCheck, Trash2 } from 'lucide-react';
import type {
  BackupHealth,
  PlatformBackup,
  PlatformBackupSettings,
} from '@simpleacs/shared';
import { api } from '../../lib/api';
import { formatBytes, formatRelative, formatTimestamp } from '../../lib/format';
import {
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  Field,
  Input,
  PageHeader,
  Pill,
  Spinner,
  StatCard,
  TBody,
  THead,
  TRow,
  Table,
} from '../../components/ui';

interface Finding {
  id: string;
  kind: string;
  subject: string;
  summary: string;
  createdAt: number;
}

interface BackupsView {
  items: PlatformBackup[];
  settings: PlatformBackupSettings;
  health: BackupHealth;
  findings: Finding[];
  storeDriver: string;
}

/**
 * Where the platform's own backups are watched.
 *
 * The page leads with AGE, not with a list: the disaster this feature exists
 * for is a job that stopped months ago while the screen still looked full of
 * backups. So the first thing on it is how old the last VERIFIED copy is and
 * whether it left this server, and both go red rather than grey when the
 * answer is bad.
 *
 * There is no restore button, deliberately. Restoring onto a fresh box is a
 * supervised job with decisions in it; the procedure is printed at the bottom
 * and travels inside every backup set.
 */
export function BackupsPage() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['platform-backups'],
    queryFn: () => api.get<BackupsView>('/platform-backups'),
    refetchInterval: 60_000,
  });
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['platform-backups'] });

  const backupNow = useMutation({
    mutationFn: () => api.post<PlatformBackup>('/platform-backups'),
    onSuccess: (row) => {
      setError(null);
      setNotice(
        row.status === 'verified'
          ? `Backup verified — ${formatBytes(row.dbBytes)}${row.storageKey ? ', mirrored off this server.' : ', on this server only.'}`
          : `The backup did not complete: ${row.error ?? 'unknown reason'}`,
      );
      void refresh();
    },
    onError: (err: Error) => setError(err.message),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/platform-backups/${id}`),
    onSuccess: () => {
      setError(null);
      setNotice('Backup set deleted.');
      void refresh();
    },
    onError: (err: Error) => setError(err.message),
  });

  const data = query.data;
  const health = data?.health;

  return (
    <>
      <PageHeader
        title="Backups"
        description="The platform's own disaster-recovery copy, and where OLT config captures are kept."
        actions={
          <Button
            variant="primary"
            icon={<Archive className="size-4" />}
            loading={backupNow.isPending}
            onClick={() => backupNow.mutate()}
          >
            Back up now
          </Button>
        }
      />

      {query.isLoading ? (
        <div className="flex justify-center p-16">
          <Spinner className="size-6" />
        </div>
      ) : query.error ? (
        <ErrorNotice error={query.error} />
      ) : !data || !health ? null : (
        <div className="space-y-5">
          {error && <ErrorNotice error={new Error(error)} />}
          {notice && (
            <p className="rounded-lg border border-line bg-surface-2 px-4 py-2.5 text-sm text-ink">
              {notice}
            </p>
          )}

          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard
              label="Last verified backup"
              value={
                health.platform.lastSuccessAt
                  ? formatRelative(health.platform.lastSuccessAt)
                  : 'never'
              }
              icon={<ShieldCheck className="size-4" />}
              tone={health.platform.stale ? 'danger' : 'success'}
              hint={
                health.platform.lastSuccessAt
                  ? formatTimestamp(health.platform.lastSuccessAt)
                  : 'The platform has never completed a backup of itself.'
              }
            />
            <StatCard
              label="Off this server"
              value={health.platform.offBox ? 'yes' : 'no'}
              icon={<CloudUpload className="size-4" />}
              tone={health.platform.offBox ? 'success' : 'warn'}
              hint={
                health.platform.offBox
                  ? `Mirrored to ${health.store.driver}.`
                  : 'A backup on the machine it protects does not survive losing that machine.'
              }
            />
            <StatCard
              label="Verified sets kept"
              value={health.platform.verifiedCount}
              icon={<HardDrive className="size-4" />}
              hint={`Keeping ${data.settings.keep}; the newest verified set can never be pruned or deleted.`}
            />
            <StatCard
              label="OLT configs in the database"
              value={health.oltConfigs.inDatabase}
              tone={health.oltConfigs.inDatabase > 0 ? 'warn' : 'success'}
              hint={`${formatBytes(health.oltConfigs.databaseBytes)} of config text; ${health.oltConfigs.onStore} of ${health.oltConfigs.total} captures are on the file store.`}
            />
          </div>

          {data.findings.length > 0 && (
            <Card title="Needs attention">
              <ul className="divide-y divide-line">
                {data.findings.map((f) => (
                  <li key={f.id} className="flex items-start gap-3 px-5 py-3">
                    <AlertTriangle className="mt-0.5 size-4 shrink-0 text-danger" />
                    <span>
                      <span className="block text-sm font-medium text-ink">{f.subject}</span>
                      <span className="block text-xs text-muted">{f.summary}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          <SecretKeyWarning envMode={data.settings.envMode} />

          <Card title="Backup sets">
            {data.items.length === 0 ? (
              <EmptyState
                title="No backups yet"
                description="Nothing has been captured. Take one now, or wait for tonight's scheduled run."
              />
            ) : (
              <Table>
                <THead>
                  <th className="th">Taken</th>
                  <th className="th">Kind</th>
                  <th className="th">State</th>
                  <th className="th">Database</th>
                  <th className="th">Other files</th>
                  <th className="th">Off-box</th>
                  <th className="th" />
                </THead>
                <TBody>
                  {data.items.map((row) => (
                    <TRow key={row.id}>
                      <td className="td">
                        <span className="block text-sm text-ink">{formatTimestamp(row.startedAt)}</span>
                        <span className="block text-xs text-faint">
                          {formatRelative(row.startedAt)}
                          {row.createdBy ? ` · ${row.createdBy}` : ''}
                        </span>
                      </td>
                      <td className="td text-sm text-muted">{row.kind}</td>
                      <td className="td">
                        <Pill state
                          tone={
                            row.status === 'verified'
                              ? 'success'
                              : row.status === 'failed'
                                ? 'danger'
                                : 'neutral'
                          }
                          dot
                        >
                          {row.status}
                        </Pill>
                        <span className="mt-1 block max-w-[24rem] text-xs text-faint">
                          {row.error ?? row.verifyDetail ?? ''}
                        </span>
                      </td>
                      <td className="td text-sm text-muted">{formatBytes(row.dbBytes)}</td>
                      <td className="td text-sm text-muted">
                        {row.includesFiles ? `${row.fileCount} · ${formatBytes(row.filesBytes)}` : '—'}
                      </td>
                      <td className="td">
                        {row.storageKey ? (
                          <Pill tone="success">mirrored</Pill>
                        ) : (
                          <span className="text-xs text-faint" title={row.mirrorError ?? ''}>
                            this server only
                          </span>
                        )}
                      </td>
                      <td className="td text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-danger"
                          icon={<Trash2 className="size-4" />}
                          loading={remove.isPending && remove.variables === row.id}
                          onClick={() => {
                            // A disaster-recovery set, deleted from the local
                            // archive AND the object store in one click. The
                            // newest verified set is undeletable server-side,
                            // so a slip costs one older copy — but every
                            // comparable delete here confirms, and this one
                            // names the set it is about to lose.
                            if (
                              confirm(
                                `Delete the backup from ${formatTimestamp(row.startedAt)}? The archive and its off-server copies are removed — there is no undo.`,
                              )
                            ) {
                              remove.mutate(row.id);
                            }
                          }}
                        >
                          Delete
                        </Button>
                      </td>
                    </TRow>
                  ))}
                </TBody>
              </Table>
            )}
          </Card>

          <ScheduleCard settings={data.settings} onSaved={refresh} />
          <OltConfigCard health={health} onChanged={refresh} />
          <RestoreProcedureCard />
        </div>
      )}
    </>
  );
}

/**
 * The one thing an operator must be told before they trust any of this.
 *
 * The database in the bucket is encrypted with `APP_SECRET_KEY`, which lives
 * in `.env`. Shipping both to one bucket would hand plaintext OLT credentials
 * to whoever gets the bucket, so `.env` is left out — and that choice has a
 * price at recovery time which the operator has to know about NOW, not while
 * restoring.
 */
function SecretKeyWarning({ envMode }: { envMode: PlatformBackupSettings['envMode'] }) {
  return (
    <Card title="What is not in the backup">
      <div className="space-y-3 px-5 py-4 text-sm text-muted">
        <p className="flex items-start gap-2">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warn" />
          <span>
            <strong className="text-ink">
              Keep a copy of <code>APP_SECRET_KEY</code> somewhere else — a password manager, not
              this server and not this bucket.
            </strong>{' '}
            OLT credentials, WireGuard private keys, ACS passwords and stored CPE passphrases are
            encrypted with it. Without it a restored database comes back complete but every one of
            those has to be re-entered by hand.
          </span>
        </p>
        <p>
          {envMode === 'local-only'
            ? '`.env` is captured on this server only and is never mirrored to object storage. If this machine is lost, you do not have it.'
            : '`.env` is deliberately excluded. The database in the backup is encrypted with the key that file holds, so one bucket containing both would yield plaintext to anyone who obtained the bucket.'}
        </p>
        <p>
          GenieACS&apos;s own database is not included either. Device parameter caches rebuild from
          the next inform — the ONUs are the source of truth there.
        </p>
      </div>
    </Card>
  );
}

function ScheduleCard({
  settings,
  onSaved,
}: {
  settings: PlatformBackupSettings;
  onSaved: () => void;
}) {
  const [form, setForm] = useState(settings);
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () =>
      api.put<PlatformBackupSettings>('/platform-backups/settings', {
        enabled: form.enabled,
        hour: form.hour,
        keep: form.keep,
        localDir: form.localDir,
        includeFiles: form.includeFiles,
        envMode: form.envMode,
      }),
    onSuccess: () => {
      setError(null);
      onSaved();
    },
    onError: (err: Error) => setError(err.message),
  });

  return (
    <Card
      title="Schedule"
      action={
        <Button size="sm" loading={save.isPending} onClick={() => save.mutate()}>
          Save
        </Button>
      }
    >
      <div className="grid gap-4 px-5 py-4 sm:grid-cols-2 xl:grid-cols-3">
        <Field label="Scheduled backup" hint="Switching this off stops the platform backing itself up.">
          <select
            className="input"
            value={form.enabled ? 'on' : 'off'}
            onChange={(e) => setForm({ ...form, enabled: e.target.value === 'on' })}
          >
            <option value="on">Enabled</option>
            <option value="off">Disabled</option>
          </select>
        </Field>
        <Field label="Not before (hour)" hint="Server local time. One run per day, after this hour.">
          <Input
            type="number"
            min={0}
            max={23}
            value={form.hour}
            onChange={(e) => setForm({ ...form, hour: Number(e.target.value) })}
          />
        </Field>
        <Field label="Sets to keep" hint="The newest verified set is never pruned, whatever this says.">
          <Input
            type="number"
            min={2}
            max={60}
            value={form.keep}
            onChange={(e) => setForm({ ...form, keep: Number(e.target.value) })}
          />
        </Field>
        <Field
          label="Backup directory"
          hint="On this server. Keep it off the disk the platform itself lives on where you can."
        >
          <Input
            value={form.localDir}
            onChange={(e) => setForm({ ...form, localDir: e.target.value })}
          />
        </Field>
        <Field
          label="Include the file store"
          hint="Only applies when the store is a directory on this server — with object storage those files are already off-box."
        >
          <select
            className="input"
            value={form.includeFiles ? 'on' : 'off'}
            onChange={(e) => setForm({ ...form, includeFiles: e.target.value === 'on' })}
          >
            <option value="on">Include</option>
            <option value="off">Leave out</option>
          </select>
        </Field>
        <Field
          label=".env"
          hint="Never mirrored to object storage under either setting — the database there is encrypted with the key it holds."
        >
          <select
            className="input"
            value={form.envMode}
            onChange={(e) =>
              setForm({ ...form, envMode: e.target.value as PlatformBackupSettings['envMode'] })
            }
          >
            <option value="exclude">Exclude (recommended)</option>
            <option value="local-only">Keep a copy on this server only</option>
          </select>
        </Field>
      </div>
      {error && (
        <p className="border-t border-line px-5 py-2.5 text-sm text-danger">{error}</p>
      )}
    </Card>
  );
}

/**
 * Part one of the feature, given its own card because it is a one-time move
 * with a destructive second half.
 *
 * Copying is safe and repeatable; clearing the database text is the only
 * thing here that destroys anything, so it is separate, password-confirmed,
 * and re-verifies every stored copy against the text it is about to empty.
 */
function OltConfigCard({
  health,
  onChanged,
}: {
  health: BackupHealth;
  onChanged: () => void;
}) {
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const carry = useMutation({
    mutationFn: () =>
      api.post<{ carried: number; failed: number; bytes: number; errors: Array<{ error: string }> }>(
        '/platform-backups/olt-bodies/carry',
      ),
    onSuccess: (r) => {
      setError(null);
      setMessage(
        `Copied ${r.carried} config${r.carried === 1 ? '' : 's'} (${formatBytes(r.bytes)}) to the file store${r.failed > 0 ? `; ${r.failed} could not be copied: ${r.errors[0]?.error ?? ''}` : '. Nothing was deleted.'}`,
      );
      onChanged();
    },
    onError: (err: Error) => setError(err.message),
  });

  const drop = useMutation({
    mutationFn: () =>
      api.post<{ cleared: number; refused: number; bytesFreed: number }>(
        '/platform-backups/olt-bodies/drop-content',
        { password },
      ),
    onSuccess: (r) => {
      setError(null);
      setPassword('');
      setMessage(
        `Cleared ${r.cleared} config${r.cleared === 1 ? '' : 's'} from the database, freeing ${formatBytes(r.bytesFreed)}${r.refused > 0 ? `; ${r.refused} kept their text because the stored copy did not match.` : '.'}`,
      );
      onChanged();
    },
    onError: (err: Error) => setError(err.message),
  });

  const pending = health.oltConfigs.inDatabase;

  return (
    <Card title="OLT config captures">
      <div className="space-y-4 px-5 py-4 text-sm text-muted">
        <p>
          {health.oltConfigs.onStore} of {health.oltConfigs.total} captures are on the file store.{' '}
          {pending > 0
            ? `${pending} still hold their config in the database (${formatBytes(health.oltConfigs.databaseBytes)}).`
            : 'Nothing is left in the database.'}
        </p>
        {health.oltConfigs.staleOlts.length > 0 && (
          <p className="text-danger">
            No recent config backup for:{' '}
            {health.oltConfigs.staleOlts.map((o) => o.name).join(', ')}.
          </p>
        )}
        <div className="flex flex-wrap items-end gap-3">
          <Button loading={carry.isPending} disabled={pending === 0} onClick={() => carry.mutate()}>
            Copy to the file store
          </Button>
          <span className="text-xs text-faint">
            Copies, reads back and compares each one. Nothing is deleted.
          </span>
        </div>
        <div className="flex flex-wrap items-end gap-3 border-t border-line pt-4">
          <Field
            label="Clear the database copies"
            hint="Only clears a row whose stored copy re-reads byte-for-byte identical. Confirm with your password."
          >
            <Input
              type="password"
              autoComplete="current-password"
              placeholder="Your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>
          <Button
            variant="danger"
            loading={drop.isPending}
            disabled={password.length === 0 || health.oltConfigs.onStore === 0}
            onClick={() => drop.mutate()}
          >
            Clear
          </Button>
        </div>
        {message && <p className="text-ink">{message}</p>}
        {error && <p className="text-danger">{error}</p>}
      </div>
    </Card>
  );
}

function RestoreProcedureCard() {
  const query = useQuery({
    queryKey: ['platform-backups', 'restore-procedure'],
    queryFn: () => api.get<{ markdown: string }>('/platform-backups/restore-procedure'),
  });
  return (
    <Card title="Restoring onto a fresh server">
      <p className="border-b border-line px-5 py-2.5 text-xs text-faint">
        Operator documentation, not a button. The same text ships inside every backup set as
        <code> manifest.restore</code>, because the server holding the repository is exactly the one
        that is missing when this is needed.
      </p>
      <pre className="max-h-[32rem] overflow-auto whitespace-pre-wrap px-5 py-4 text-xs leading-relaxed text-muted">
        {query.data?.markdown ?? (query.error ? 'Could not be read.' : 'Loading…')}
      </pre>
    </Card>
  );
}
